import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, CaretLeft as ChevronLeft, CaretRight as ChevronRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import { CaddySidebar } from "../components/CaddySidebar";
import { CaddyBottomNav } from "../components/CaddyBottomNav";
import { SeasonLockedScreen } from "../components/game/SeasonLockedScreen";
import { homeSection } from '../lib/marketingHome';
import { mask, usePrivacy } from '../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  bg: "var(--pr-bg)",
  ink: "var(--pr-ink)",
  ink2: "var(--pr-ink2)",
  muted: "var(--pr-muted)",
  line: "var(--pr-line)",
  card: "var(--pr-card)",
  gold: "var(--pr-gold)",
  red: "var(--pr-red)",
  green: "var(--pr-green)",
};

const fmt = (n) => mask(`$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmt0 = (n) => mask(`$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const dayLbl = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const dotColor = (st) =>
  st === "green" || st === "cleared" ? COLOR.green : st === "near" ? COLOR.gold : st === "red" ? COLOR.red : COLOR.line;

export default function MonthlyWrapPage() {
  usePrivacy();  // re-render when Creator Privacy Mode flips
  const navigate = useNavigate();
  const [view, setView] = useState("loading"); // loading | not_member | ready
  const [data, setData] = useState(null);
  const [navBusy, setNavBusy] = useState(false);

  const load = async (month) => {
    setNavBusy(true);
    try {
      const res = await fetch(`${API}/api/caddy/monthly${month ? `?month=${month}` : ""}`, { credentials: "include" });
      if (res.status === 401) { navigate("/login?next=/app/monthly", { replace: true }); return; }
      if (res.status === 403) {
        const body = await res.json().catch(() => null);
        setView(body?.detail?.code === "season_pass_required" ? "sprint_gate" : "not_member");
        return;
      }
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Couldn't load your month"); return; }
      setData(d);
      setView("ready");
    } catch {
      if (data) toast.error("Couldn't load that month, check your connection and try again");
      else setView("error");
    } finally {
      setNavBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (view === "error") return <CaddyErrorCard />;

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }}>
        <p className="text-sm" style={{ color: COLOR.ink2 }}>Adding up your month…</p>
      </div>
    );
  }

  if (view === "sprint_gate") {
    return (
      <SeasonLockedScreen
        id="monthly-sprint-gate"
        icon="📅"
        title="Monthly Review"
        body="Your Sprint is four relays in one tab, there's no month to wrap yet. In the full 52-week race, every month closes with a wrap-up you can actually feel."
        bullets={["Every relay in the month, red / black / green at a glance", "What you set aside for taxes, PTO, savings and debt", "The bonus a fully-cleared month drops into your Bonus Bank"]}
      />
    );
  }

  if (view === "not_member") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="max-w-sm text-center" data-testid="monthly-join-gate">
          <h1 className="text-2xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            The Monthly Review rides with Payday Relay
          </h1>
          <Link to={homeSection('season-pass')} className="inline-block px-8 py-3 rounded-full font-bold" style={{ background: "var(--pr-ink-surface)", color: "#fff" }}>
            See membership options →
          </Link>
        </div>
      </div>
    );
  }

  const t = data.totals;
  const monthName = data.label.split(" ")[0];

  return (
    <div className="min-h-screen" style={{ background: "var(--pr-bg)" }} data-testid="monthly-wrap-page">
      <header className="px-5 pt-5 pb-2 lg:hidden">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-1.5 text-sm hover:opacity-70" style={{ color: COLOR.ink }} data-testid="monthly-back-link">
            <ArrowLeft className="w-4 h-4" /> Payday Relay
          </Link>
          <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: COLOR.muted }}>📆 Monthly Review</p>
        </div>
      </header>

      <div className="w-full max-w-7xl mx-auto lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 lg:px-8 lg:pt-8">
        <CaddySidebar active="monthly" />

        <main className="px-5 py-6 lg:px-0 lg:pt-0 pb-16">
          <div className="max-w-md mx-auto lg:max-w-3xl lg:mx-0">
            {/* What this page is, reviewers found it unclear on desktop */}
            <div className="hidden lg:block mb-6">
              <h1 className="text-3xl lg:text-4xl mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }} data-testid="monthly-page-title">
                Monthly Review
              </h1>
              <p className="text-sm max-w-xl leading-relaxed" style={{ color: COLOR.ink2 }} data-testid="monthly-page-intro">
                Every relay that landed inside one calendar month, in one view. A relay is one earning week, 
                a month holds four or five of them. Months that finish net positive feed your Bonus Bank.
              </p>
            </div>

            <div className="space-y-4">
              {/* Month navigation */}
              <div className="flex items-center justify-center lg:justify-start gap-3">
                <button
                  onClick={() => load(data.prev_month)}
                  disabled={navBusy}
                  className="p-2 rounded-full border disabled:opacity-30 hover:bg-black/5"
                  style={{ borderColor: COLOR.line }}
                  aria-label="Previous month"
                  data-testid="monthly-prev"
                >
                  <ChevronLeft className="w-4 h-4" style={{ color: COLOR.ink }} />
                </button>
                <h2 className="text-2xl text-center min-w-[180px] lg:min-w-0 lg:text-left" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }} data-testid="monthly-label">
                  {data.label}
                </h2>
                <button
                  onClick={() => data.next_month && load(data.next_month)}
                  disabled={!data.next_month || navBusy}
                  className="p-2 rounded-full border disabled:opacity-30 hover:bg-black/5"
                  style={{ borderColor: COLOR.line }}
                  aria-label="Next month"
                  data-testid="monthly-next"
                >
                  <ChevronRight className="w-4 h-4" style={{ color: COLOR.ink }} />
                </button>
              </div>

              {data.weeks.length === 0 ? (
                <div className="text-center rounded-2xl p-5 lg:p-6 pr-card" data-testid="monthly-empty">
                  <p className="text-sm font-bold mb-1" style={{ color: COLOR.ink }}>No relays landed in {data.label}</p>
                  <p className="text-xs" style={{ color: COLOR.ink2 }}>Flip to another month, or head back and log some wins.</p>
                </div>
              ) : (
                <div className="lg:grid lg:grid-cols-2 lg:gap-4 space-y-4 lg:space-y-0 items-start">
                  {/* Totals card */}
                  <div className="rounded-2xl p-6 text-center border" style={{ background: "var(--pr-card)", borderColor: COLOR.line }} data-testid="monthly-totals">
                    <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.muted }}>Earned in {monthName}</p>
                    <p className="text-5xl font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }} data-testid="monthly-earned-total">
                      {fmt(t.earned)}
                    </p>
                    {t.target_total > 0 && (
                      <p className="text-[11px] mt-1" style={{ color: COLOR.muted }}>
                        against {fmt(t.target_total)} in weekly targets
                      </p>
                    )}
                    <div className="grid grid-cols-3 gap-2 mt-5 pt-4 border-t" style={{ borderColor: COLOR.line }}>
                      <div>
                        <p className="text-lg font-bold" style={{ color: COLOR.ink }} data-testid="monthly-cleared-stat">🔥 {t.cleared}/{t.weeks}</p>
                        <p className="text-[10px] uppercase tracking-wide" style={{ color: COLOR.muted }}>relays cleared</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold" style={{ color: t.surplus > 0 ? COLOR.green : COLOR.ink }} data-testid="monthly-surplus-stat">{fmt0(t.surplus)}</p>
                        <p className="text-[10px] uppercase tracking-wide" style={{ color: COLOR.muted }}>bonus banked</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold" style={{ color: COLOR.ink }} data-testid="monthly-best-stat">{data.best_week ? fmt0(data.best_week.earned) : "None yet"}</p>
                        <p className="text-[10px] uppercase tracking-wide" style={{ color: COLOR.muted }}>best week</p>
                      </div>
                    </div>
                  </div>

                  {/* Relay by relay */}
                  <div className="rounded-2xl p-5 lg:p-6 pr-card" data-testid="monthly-weeks">
                    <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: COLOR.muted }}>Relay by relay</p>
                    <p className="text-[11px] mb-3" style={{ color: COLOR.muted }}>One row per earning week in {monthName}.</p>
                    <div className="space-y-2">
                      {data.weeks.map((w) => (
                        <div key={w.week_start} className="flex items-center justify-between text-sm" data-testid={`monthly-week-${w.week_start}`}>
                          <span className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" style={{ background: dotColor(w.status) }} />
                            <span style={{ color: COLOR.ink }}>
                              {w.relay_number ? `Relay #${w.relay_number}` : "Week"}
                              <span style={{ color: COLOR.muted }}> · {dayLbl(w.week_start)}–{dayLbl(w.week_end)}</span>
                            </span>
                          </span>
                          <span className="font-bold" style={{ color: w.status === "green" || w.status === "cleared" ? COLOR.green : COLOR.ink }}>
                            {fmt(w.earned)}{w.in_progress ? <span className="font-normal text-[10px]" style={{ color: COLOR.muted }}> so far</span> : null}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      <div className="h-20 lg:hidden" />
      <CaddyBottomNav active="more" />
    </div>
  );
}
